// ./src/components/features/Contacts/PersonGroupSelect/PersonGroupSelect.tsx
//
// Group picker for the contact editor: pick an existing PersonGroup, create one
// IN PLACE, or edit / remove the selected group (a small inline name + kind form)
// without leaving the drawer. Reads groups + create/update/remove from the
// optional PersonGroupsContext. Removing a group detaches it from every contact
// and membership server-side (PersonGroupCore cascade).

import React, { useMemo, useState, useCallback } from "react";
import { Stack, Group, TextInput, Box, Text, ActionIcon, Tooltip } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { PERSON_GROUP_KIND_VALUES } from "@/utils/api";
import { DRAWER_COMBOBOX_PROPS, recordHasRight, refusalReason } from "@/utils";
import { useOptionalPersonGroupsContext } from "@/contexts";
import { AppButton, AppIcon, ConfirmModal, SelectField } from "@/components/generic";
import { useCreateInPlace } from "@/hooks/useCreateInPlace";
import { CREATE_IN_OWN_ACCOUNT } from "@/utils/createInPlace";

export interface PersonGroupSelectProps {
  /** Selected group id (or null). */
  value: string | null;
  /** Called with the chosen (or newly created) group id, or null. */
  onChange: (id: string | null) => void;
  label?: string;
  description?: string;
  error?: string;
  disabled?: boolean;
}

// Sentinel appended to the options list to enter create-in-place mode.

type Mode = "select" | "create" | "edit";

export const PersonGroupSelect: React.FC<PersonGroupSelectProps> = ({
  value,
  onChange,
  label,
  description,
  error,
  disabled = false,
}) => {
  const { t } = useTranslation(["contacts", "common"]);
  // #142 — a contact group lands in the caller's own account: never offered to a crew member.
  const canCreate = useCreateInPlace(CREATE_IN_OWN_ACCOUNT);
  const ctx = useOptionalPersonGroupsContext();

  // ⓘ Memoised: a fresh `[]` on every render would change what the memos below depend on (#129).
  const groups = useMemo(() => ctx?.personGroups ?? [], [ctx?.personGroups]);
  const isCreatingGroup = ctx?.isCreating ?? false;
  const isUpdatingGroup = ctx?.isUpdating ?? false;
  const isRemovingGroup = ctx?.isRemoving ?? false;

  const [mode, setMode] = useState<Mode>("select");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<string>("household");
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const selectedGroup = useMemo(() => groups.find((g) => g.id === value) ?? null, [groups, value]);
  const canEditSelected = !!selectedGroup && recordHasRight(selectedGroup, "edit");
  const canRemoveSelected = !!selectedGroup && recordHasRight(selectedGroup, "remove");

  const options = useMemo(() => {
    return groups.map((g) => ({ value: g.id!, label: g.name ?? g.id! }));
  }, [groups]);

  const kindOptions = useMemo(
    () =>
      PERSON_GROUP_KIND_VALUES.map((k) => ({
        value: k,
        label: t(`contacts:group.kind.${k}`, { defaultValue: k.charAt(0).toUpperCase() + k.slice(1) }),
      })),
    [t]
  );

  const handleSelect = useCallback(
    (next: string | null) => {
      onChange(next);
    },
    [onChange]
  );

  const startCreate = useCallback(() => {
    setMode("create");
    setName("");
    setKind("household");
    setFormError(null);
  }, []);

  const startEdit = useCallback(() => {
    if (!selectedGroup) {
      return;
    }

    setMode("edit");
    setName(selectedGroup.name ?? "");
    setKind(selectedGroup.kind ?? "household");
    setFormError(null);
  }, [selectedGroup]);

  const cancelForm = useCallback(() => {
    setMode("select");
    setName("");
    setKind("household");
    setFormError(null);
  }, []);

  const submitForm = useCallback(async () => {
    const trimmed = name.trim();

    if (!trimmed) {
      setFormError(t("contacts:group.nameRequired", { defaultValue: "A group name is required" }));

      return;
    }

    setFormError(null);

    if (mode === "edit" && selectedGroup?.id && ctx?.update) {
      const result = await ctx.update(selectedGroup.id, { name: trimmed, kind });

      if (result.didSucceed()) {
        cancelForm();

        return;
      }

      setFormError(
        refusalReason(result) || t("contacts:group.updateFailed", { defaultValue: "Couldn't update the group" })
      );

      return;
    }

    if (mode === "create" && ctx?.create) {
      const result = await ctx.create({ name: trimmed, kind });

      if (result.didSucceed()) {
        const created = result.getDataFirst() as { id?: string } | undefined;

        if (created?.id) {
          onChange(created.id);
        }

        cancelForm();

        return;
      }

      setFormError(
        refusalReason(result) || t("contacts:group.createFailed", { defaultValue: "Couldn't create the group" })
      );
    }
  }, [name, kind, mode, selectedGroup, ctx, onChange, cancelForm, t]);

  const handleConfirmRemove = useCallback(async () => {
    if (!selectedGroup?.id || !ctx?.remove) {
      setConfirmRemove(false);

      return;
    }

    const result = await ctx.remove(selectedGroup.id);
    setConfirmRemove(false);

    if (result.didSucceed()) {
      // The group is gone (and detached from every contact) — clear the picker.
      onChange(null);
    }
  }, [selectedGroup, ctx, onChange]);

  // ── Inline create / edit form ──
  if (mode === "create" || mode === "edit") {
    const busy = mode === "edit" ? isUpdatingGroup : isCreatingGroup;

    return (
      <Box>
        <Text size="md" fw={500} mb={4}>
          {mode === "edit"
            ? t("contacts:group.editTitle", { defaultValue: "Edit group" })
            : t("contacts:group.newTitle", { defaultValue: "New group" })}
        </Text>
        <Stack gap="xs">
          <TextInput
            placeholder={t("contacts:group.namePlaceholder", { defaultValue: "e.g. The Smiths / Acme Inc." })}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            error={formError ?? undefined}
            data-autofocus
            size="md"
          />
          <SelectField
            aria-label={t("contacts:groups.kind", { defaultValue: "Kind of group" })}
            data={kindOptions}
            value={kind}
            onChange={(_name, next) => setKind(next || "household")}
            comboboxProps={DRAWER_COMBOBOX_PROPS}
            allowDeselect={false}
            size="md"
          />
          <Group gap="xs" justify="flex-end">
            <AppButton variant="secondary" size="md" onClick={cancelForm} disabled={busy}>
              {t("common:buttons.cancel", { defaultValue: "Cancel" })}
            </AppButton>
            <AppButton variant="primary" size="md" iconName="Check" onClick={submitForm} inProgress={busy}>
              {mode === "edit"
                ? t("common:buttons.save", { defaultValue: "Save" })
                : t("contacts:group.create", { defaultValue: "Create group" })}
            </AppButton>
          </Group>
        </Stack>
      </Box>
    );
  }

  // ── Picker (+ edit / remove of the selected group) ──
  return (
    <>
      <Group gap="xs" align="flex-end" wrap="nowrap">
        <Box style={{ flex: 1, minWidth: 0 }}>
          <SelectField
            label={label ?? t("contacts:group.label", { defaultValue: "Group / household" })}
            description={description}
            placeholder={t("contacts:group.placeholder", { defaultValue: "No group" })}
            data={options}
            value={value}
            onChange={(_n, next) => handleSelect(next)}
            error={error}
            disabled={disabled}
            createLabel={canCreate ? t("contacts:group.createOption", { defaultValue: "Create a new group" }) : undefined}
            onCreate={canCreate ? startCreate : undefined}
            clearable
            searchable
            comboboxProps={DRAWER_COMBOBOX_PROPS}
            leftSection={<AppIcon iconName="UsersGroup" size={16} />}
            size="md"
          />
        </Box>
        {selectedGroup && (canEditSelected || canRemoveSelected) && (
          <Group gap={4} wrap="nowrap" pb={4}>
            {canEditSelected && (
              <Tooltip label={t("contacts:group.edit", { defaultValue: "Edit group" })} withArrow>
                <ActionIcon
                  variant="subtle"
                  color="blue"
                  size="lg"
                  onClick={startEdit}
                  disabled={disabled || isRemovingGroup}
                  aria-label={t("contacts:group.edit", { defaultValue: "Edit group" })}
                >
                  <AppIcon iconName="Edit" size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            {canRemoveSelected && (
              <Tooltip label={t("contacts:group.remove", { defaultValue: "Remove group" })} withArrow>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="lg"
                  onClick={() => setConfirmRemove(true)}
                  disabled={disabled}
                  loading={isRemovingGroup}
                  aria-label={t("contacts:group.remove", { defaultValue: "Remove group" })}
                >
                  <AppIcon iconName="Trash" size={16} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        )}
      </Group>

      <ConfirmModal
        opened={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={handleConfirmRemove}
        title={t("contacts:group.removeTitle", { defaultValue: "Remove group" })}
        message={t("contacts:group.removeMessage", {
          name: selectedGroup?.name ?? "",
          defaultValue:
            'Remove the group "{{name}}"? It will be detached from every contact and membership that uses it. This can\'t be undone.',
        })}
        confirmInProgress={isRemovingGroup}
        confirmLabel={t("common:buttons.remove", { defaultValue: "Remove" })}
        type="danger"
      />
    </>
  );
};
